import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listOpenExamSessionsForStudent } from '../services/exam2Api'
import { useResponsiveContext } from '../context/ResponsiveContext'

const cardStyle = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: 12,
  background: '#ffffff',
}

const EXAM_TYPE_LABELS = {
  daily: 'يومي',
  monthly: 'شهري',
  midyear: 'نصف السنة',
  final: 'نهائي',
}

function attemptStatusLabel(session) {
  const status = session.attempt_status
  const attemptsAllowed = session.attempts_allowed
  const attemptCount = session.attempt_count

  if (!status) {
    return 'لم تبدأ بعد'
  }

  if (status === 'in_progress') return 'محاولة جارية'
  if (status === 'submitted') return 'تم الإرسال، بانتظار التصحيح'
  if (status === 'graded') return 'تم التصحيح'

  return status
}

function ctaLabel(session) {
  const status = session.attempt_status
  const attemptsAllowed = session.attempts_allowed
  const attemptCount = session.attempt_count

  // No attempts yet
  if (!status) return 'بدء الامتحان'

  if (status === 'in_progress') return 'استئناف الامتحان'

  // Submitted / graded
  if (attemptsAllowed != null && typeof attemptCount === 'number') {
    if (attemptCount >= attemptsAllowed) {
      // No more attempts allowed – treat as view-only entry point
      return 'عرض صفحة الامتحان'
    }
    // Attempts remaining
    return 'بدء محاولة جديدة'
  }

  return 'دخول صفحة الامتحان'
}

export default function Exams() {
  const navigate = useNavigate()
  const { isMobile } = useResponsiveContext()

  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      setErrorMsg('')
      try {
        const data = await listOpenExamSessionsForStudent()
        if (!cancelled) setSessions(data || [])
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e?.message || 'فشل تحميل الامتحانات')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [])

  const now = useMemo(() => new Date(), [])

  const buckets = useMemo(() => {
    const current = []
    const upcoming = []
    const past = []

    for (const s of sessions) {
      const starts = s.starts_at ? new Date(s.starts_at) : null
      const ends = s.ends_at ? new Date(s.ends_at) : null
      if (!starts || !ends) {
        current.push(s)
        continue
      }
      if (now >= starts && now <= ends) current.push(s)
      else if (now < starts) upcoming.push(s)
      else past.push(s)
    }

    return { current, upcoming, past }
  }, [sessions, now])

  function renderList(list) {
    if (!list.length) return <div style={{ padding: 8, color: '#6b7280' }}>لا توجد امتحانات في هذه القائمة.</div>
    return (
      <div style={{ display: 'grid', gap: 10 }}>
        {list.map(s => {
          const starts = s.starts_at ? new Date(s.starts_at).toLocaleString('ar-EG') : '—'
          const ends = s.ends_at ? new Date(s.ends_at).toLocaleString('ar-EG') : '—'
          return (
            <div key={s.session_id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ fontWeight: 700 }}>
                  {s.title || s.paper_title || 'امتحان بدون عنوان'}
                </div>
                <div
                  style={{
                    padding: '2px 10px',
                    borderRadius: 999,
                    border: '1px solid #d1d5db',
                    background: '#f9fafb',
                    fontSize: 11,
                    color: '#374151',
                  }}
                >
                  {attemptStatusLabel(s)}
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 4 }}>
                {s.exam_type && (
                  <span>
                    نوع الامتحان: {EXAM_TYPE_LABELS[s.exam_type] || s.exam_type}
                  </span>
                )}
                {s.subject_name && (
                  <span style={{ marginInlineStart: 8 }}>المادة: {s.subject_name}</span>
                )}
              </div>
              <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
                من {starts} إلى {ends}
              </div>
              <div style={{ fontSize: 13, color: '#111827', marginBottom: 10 }}>
                {s.attempts_allowed != null && (
                  <span>
                    المحاولات: {s.attempt_count ?? 0} / {s.attempts_allowed}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => navigate(`/exams/${s.session_id}`)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 999,
                    border: '1px solid #1e3a8a',
                    background: '#1e3a8a',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: 13,
                    minWidth: 140,
                  }}
                >
                  {ctaLabel(s)}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: isMobile ? '1rem' : '1.5rem',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>امتحانات أونلاين</h1>
          </div>

          {errorMsg ? (
            <div style={{ padding: 10, borderRadius: 10, border: '1px solid #fecaca', background: '#fff1f2', color: '#7f1d1d', marginBottom: 12 }}>{errorMsg}</div>
          ) : null}

          {loading && (
            <div style={{ padding: 8, color: '#6b7280' }}>جاري التحميل...</div>
          )}

          {!loading && !errorMsg && (
            <div style={{ display: 'grid', gap: 16 }}>
              <section>
                <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>امتحانات حالية</h2>
                {renderList(buckets.current)}
              </section>
              <section>
                <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>امتحانات قادمة</h2>
                {renderList(buckets.upcoming)}
              </section>
              <section>
                <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>امتحانات سابقة</h2>
                {renderList(buckets.past)}
              </section>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
